import {
	Camera,
	Palette,
	TrendingUp,
	Users,
	ArrowRight,
	CheckCircle,
	Sparkles,
} from 'lucide-react'
import { Link, type LoaderFunctionArgs, redirect } from 'react-router'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import { Card, CardContent } from '#app/components/ui/card'
import { getUserId } from '#app/utils/auth.server'

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await getUserId(request)

	// If user is logged in, redirect to dashboard
	if (userId) {
		return redirect('/dashboard')
	}

	// Otherwise, show the marketing homepage
	return null
}

export const meta = () => [
	{ title: 'Woonplan - Verbouwen. Helder. Zeker.' },
	{
		name: 'description',
		content:
			'De complete verbouwapp voor Nederlandse huiseigenaren. Van idee tot oplevering.',
	},
]

const features = [
	{
		icon: Camera,
		title: 'Scan je woning in 10 minuten',
		description:
			"Upload foto's en beantwoord simpele vragen. Je krijgt direct: een 3D-model van je huis, persoonlijke moodboards, wat je kunt verbeteren, hoeveel je bespaart en wat het kost.'",
	},
	{
		icon: Palette,
		title: 'Visualiseer je droomhuis',
		description:
			'Krijg binnen 24 uur een fotorealistische 3D-weergave. Experimenteer met stijlen, indelingen en materialen.',
	},
	{
		icon: TrendingUp,
		title: 'Plan met zekerheid',
		description:
			'Timeline, budget, vergunningen - alles in één overzicht. Weet precies wat wanneer gebeurt en wat het kost.',
	},
	{
		icon: Users,
		title: 'Beheer de uitvoering',
		description:
			'Volg dagelijks de voortgang. Communiceer met aannemers. Bewaar alle documenten op één plek.',
	},
]

const stages = [
	{
		name: 'Oriëntatie',
		description: "Upload foto's, beantwoord vragen, maak moodboards",
		icon: '💭',
		outcome: 'Helder beeld van je wensen',
		duration: '1-2 weken',
	},
	{
		name: 'Ontwerp',
		description: '3D-visualisatie, plattegronden, materiaaladvies',
		icon: '✏️',
		outcome: 'Compleet uitgewerkt plan',
		duration: '2-3 weken',
	},
	{
		name: 'Voorbereiding',
		description: 'Offertes, vergunningen, planning, contracten',
		icon: '📋',
		outcome: 'Alles geregeld om te starten',
		duration: '3-4 weken',
	},
	{
		name: 'Uitvoering',
		description: 'Dagelijkse updates, voortgang volgen, communicatie',
		icon: '🔨',
		outcome: 'Je nieuwe thuis wordt werkelijkheid',
		duration: '8-12 weken',
	},
]

const faqItems = [
	{
		question: 'Hoe werkt Woonplan?',
		answer:
			"Upload foto's. Beantwoord vragen. Ontvang 3D-visualisatie binnen 24 uur. Plan, budget, voer uit. Alles in één platform.",
	},
	{
		question: 'Wat zijn de kosten?',
		answer:
			'Start gratis. Daarna vanaf €19 per maand, maandelijks opzegbaar. Geen setup kosten. Geen verborgen kosten.',
	},
	{
		question: 'Hoe snel kan ik beginnen?',
		answer:
			'Direct. Account aanmaken duurt 2 minuten. Eerste inzichten binnen 10 minuten. 3D-visualisatie binnen 24 uur.',
	},
	{
		question: 'Werken jullie met aannemers?',
		answer:
			'Ja. Netwerk van 500+ geverifieerde aannemers. Meerdere offertes. Reviews. Jij kiest.',
	},
	{
		question: 'Kan ik mijn eigen aannemer gebruiken?',
		answer:
			'Natuurlijk. Nodig ze uit in het platform. Iedereen werkt vanuit dezelfde informatie.',
	},
	{
		question: 'Helpen jullie met vergunningen?',
		answer:
			'Ja. Automatische check. Hulp bij aanvragen. Expert support beschikbaar.',
	},
	{
		question: 'Zijn er subsidies beschikbaar?',
		answer:
			'Ja. Automatische subsidiecheck. ISDE, gemeentelijk, provinciaal. Gemiddeld €3.500 per project.',
	},
	{
		question: 'Is mijn data veilig?',
		answer:
			'100% veilig. ISO 27001 gecertificeerd. Nederlandse servers. AVG-compliant. Bankniveau beveiliging.',
	},
]

export default function Index() {
	return (
		<main className="flex flex-col">
			{/* Hero Section */}
			<section className="from-primary/5 to-secondary/5 relative overflow-hidden bg-gradient-to-br via-transparent px-4 py-24">
				<div className="container mx-auto max-w-6xl">
					<div className="mx-auto max-w-4xl text-center">
						<div className="bg-primary/10 text-primary mb-6 hidden inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium">
							<Sparkles className="h-4 w-4" />
							De visuele verbouwapp voor huiseigenaren
						</div>
						<h1 className="text-foreground mb-6 text-5xl font-bold md:text-6xl">
							Maak je verbouwing visueel{' '}
							<span className="from-primary to-secondary bg-gradient-to-r bg-clip-text text-transparent">
								in jouw woonplan
							</span>
						</h1>
						<h1 className="text-foreground mb-6 hidden text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl">
							De verbouwapp voor
							<span className="from-primary to-secondary mt-2 block bg-gradient-to-r bg-clip-text text-transparent">
								huiseigenaren
							</span>
						</h1>
						<p className="text-muted-foreground mx-auto mb-10 max-w-2xl text-lg md:text-xl">
							Alles wat je nodig hebt voor een succesvolle verbouwing. Van
							eerste idee tot oplevering.
						</p>
						<div className="mb-12 flex flex-col justify-center gap-4 sm:flex-row">
							<Button asChild size="lg" className="rounded-full">
								<Link to="/start">
									Start nu gratis
									<ArrowRight className="ml-2 h-4 w-4" />
								</Link>
							</Button>
							<Button
								asChild
								variant="outline"
								size="lg"
								className="rounded-full"
							>
								<Link to="/login">Inloggen</Link>
							</Button>
						</div>
						<div className="text-muted-foreground flex flex-wrap justify-center gap-x-12 gap-y-4 text-sm">
							<div className="flex items-center gap-2">
								<CheckCircle className="h-5 w-5 text-green-600" />
								<span>Geen creditcard nodig</span>
							</div>
							<div className="flex items-center gap-2">
								<CheckCircle className="h-5 w-5 text-green-600" />
								<span>Direct beginnen</span>
							</div>
							<div className="flex items-center gap-2">
								<CheckCircle className="h-5 w-5 text-green-600" />
								<span>€2.500 gemiddelde besparing</span>
							</div>
						</div>
					</div>
					{/* Trust badges */}
					<div className="mt-20 hidden border-t pt-12">
						<p className="text-muted-foreground mb-8 text-center text-xs tracking-wide uppercase">
							Vertrouwd door de beste aannemers en architecten
						</p>
						<div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-60">
							<div className="text-muted-foreground text-2xl font-bold">
								BAM
							</div>
							<div className="text-muted-foreground text-2xl font-bold">
								Heijmans
							</div>
							<div className="text-muted-foreground text-2xl font-bold">
								VolkerWessels
							</div>
							<div className="text-muted-foreground text-2xl font-bold">
								Dura Vermeer
							</div>
							<div className="text-muted-foreground text-2xl font-bold">
								Ballast Nedam
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* How It Works */}
			<section className="px-4 py-24">
				<div className="container mx-auto max-w-6xl">
					<div className="mb-16 text-center">
						<h2 className="mb-4 text-3xl font-bold md:text-4xl">
							Alles wat je nodig hebt in één platform.
						</h2>
						<p className="text-muted-foreground mx-auto max-w-2xl text-lg">
							Van eerste idee tot oplevering, beheer je volledige verbouwing in
							alle fases.
						</p>
					</div>
					<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
						{features.map((feature) => {
							const Icon = feature.icon
							return (
								<Card
									key={feature.title}
									className="hover:border-primary/50 border-2 transition-colors"
								>
									<CardContent className="p-6">
										<div className="bg-primary/10 mb-4 flex h-12 w-12 items-center justify-center rounded-lg">
											<Icon className="text-primary h-6 w-6" />
										</div>
										<h3 className="mb-2 text-lg font-semibold">
											{feature.title}
										</h3>
										<p className="text-muted-foreground text-sm">
											{feature.description}
										</p>
									</CardContent>
								</Card>
							)
						})}
					</div>
				</div>
			</section>

			{/* Stages Section */}
			<section className="bg-secondary/5 px-4 py-24">
				<div className="container mx-auto max-w-6xl">
					<div className="mb-16 text-center">
						<h2 className="mb-4 text-3xl font-bold md:text-4xl">
							Jouw traject van A tot Z
						</h2>
						<p className="text-muted-foreground mx-auto max-w-2xl text-lg">
							Gemiddeld 3-4 maanden van eerste idee tot oplevering
						</p>
					</div>
					<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
						{stages.map((stage, index) => (
							<div key={stage.name} className="text-center">
								<div className="mb-4 text-5xl">{stage.icon}</div>
								<div className="text-muted-foreground mb-2 text-sm">
									{stage.duration}
								</div>
								<h3 className="mb-2 text-lg font-semibold">{stage.name}</h3>
								<p className="text-muted-foreground mb-3 text-sm">
									{stage.description}
								</p>
								<p className="text-primary text-xs font-medium">
									{stage.outcome}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Benefits Section */}
			<section className="px-4 py-24">
				<div className="container mx-auto max-w-6xl">
					<div className="grid items-center gap-16 lg:grid-cols-2">
						<div>
							<h2 className="mb-6 text-3xl font-bold md:text-4xl">
								Waarom huiseigenaren voor Woonplan kiezen
							</h2>
							<div className="space-y-4">
								<div className="flex gap-3">
									<CheckCircle className="h-6 w-6 flex-shrink-0 text-green-500" />
									<div>
										<h4 className="mb-1 font-semibold">Eindelijk overzicht</h4>
										<p className="text-muted-foreground text-sm">
											Alle informatie op één plek. Van inspiratie tot facturen,
											van planning tot foto's.
										</p>
									</div>
								</div>
								<div className="flex gap-3">
									<CheckCircle className="h-6 w-6 flex-shrink-0 text-green-500" />
									<div>
										<h4 className="mb-1 font-semibold">Geen verrassingen</h4>
										<p className="text-muted-foreground text-sm">
											Zie vooraf hoe het wordt. Weet wat het kost. Houd grip op
											de planning.
										</p>
									</div>
								</div>
								<div className="flex gap-3">
									<CheckCircle className="h-6 w-6 flex-shrink-0 text-green-500" />
									<div>
										<h4 className="mb-1 font-semibold">Bespaar tijd en geld</h4>
										<p className="text-muted-foreground text-sm">
											Gemiddeld €8.500 goedkoper door slimme planning. 6 weken
											sneller klaar.
										</p>
									</div>
								</div>
								<div className="flex gap-3">
									<CheckCircle className="h-6 w-6 flex-shrink-0 text-green-500" />
									<div>
										<h4 className="mb-1 font-semibold">
											Professionele begeleiding
										</h4>
										<p className="text-muted-foreground text-sm">
											Experts staan klaar om je te helpen. Van architect tot
											bouwbegeleider.
										</p>
									</div>
								</div>
							</div>
							<div className="bg-primary/5 mt-8 rounded-lg p-4">
								<p className="text-sm font-medium">
									"Woonplan gaf ons het vertrouwen om eindelijk te beginnen.
									Alles verliep soepel en we zijn super blij met het resultaat."
								</p>
								<p className="text-muted-foreground mt-2 text-xs">
									— Familie Jansen, Utrecht (Complete verdieping verbouwd)
								</p>
							</div>
						</div>
						<div className="relative">
							<div className="from-primary/20 to-secondary/20 flex aspect-square items-center justify-center rounded-3xl bg-gradient-to-br">
								<div className="p-8 text-center">
									<div className="text-primary mb-2 text-6xl font-bold">
										92%
									</div>
									<p className="text-lg font-medium">Op tijd opgeleverd</p>
									<p className="text-muted-foreground mt-2 text-sm">
										tegenover 23% landelijk
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Results Section */}
			<section className="bg-secondary/5 px-4 py-24">
				<div className="container mx-auto max-w-6xl">
					<div className="mb-12 text-center">
						<h2 className="mb-4 text-3xl font-bold md:text-4xl">
							Resultaten waar je op kunt bouwen
						</h2>
					</div>
					<div className="grid gap-8 md:grid-cols-4">
						<div className="text-center">
							<div className="text-primary mb-2 text-4xl font-bold">€8.500</div>
							<p className="text-muted-foreground text-sm">
								Gemiddeld bespaard
							</p>
						</div>
						<div className="text-center">
							<div className="text-primary mb-2 text-4xl font-bold">
								6 weken
							</div>
							<p className="text-muted-foreground text-sm">Sneller klaar</p>
						</div>
						<div className="text-center">
							<div className="text-primary mb-2 text-4xl font-bold">€3.500</div>
							<p className="text-muted-foreground text-sm">
								Subsidies ontvangen
							</p>
						</div>
						<div className="text-center">
							<div className="text-primary mb-2 text-4xl font-bold">4.9/5</div>
							<p className="text-muted-foreground text-sm">Klantwaardering</p>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section className="px-4 py-24">
				<div className="container mx-auto max-w-4xl">
					<div className="mb-12 text-center">
						<h2 className="mb-4 text-3xl font-bold md:text-4xl">
							Veelgestelde vragen
						</h2>
						<p className="text-muted-foreground text-lg">
							Alles wat je wilt weten over Woonplan
						</p>
					</div>
					<Accordion type="single" collapsible className="w-full">
						{faqItems.map((item, index) => (
							<AccordionItem key={index} value={`item-${index}`}>
								<AccordionTrigger className="text-left">
									{item.question}
								</AccordionTrigger>
								<AccordionContent>{item.answer}</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</div>
			</section>

			{/* CTA Section */}
			<section className="bg-primary/5 px-4 py-24">
				<div className="container mx-auto max-w-4xl text-center">
					<h2 className="mb-6 text-3xl font-bold tracking-tight md:text-4xl">
						Klaar om te beginnen?
					</h2>
					<p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
						Laat onze AI-specialist jouw woonproblemen analyseren. Ontvang
						direct persoonlijke oplossingen met ROI-berekeningen.
					</p>
					<div className="flex flex-col justify-center gap-4 sm:flex-row">
						<Button asChild size="lg" className="rounded-full">
							<Link to="/start">
								Start gratis analyse
								<ArrowRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
						<Button
							asChild
							variant="outline"
							size="lg"
							className="rounded-full"
						>
							<Link to="/login">Inloggen</Link>
						</Button>
					</div>
					<p className="text-muted-foreground mt-8 text-sm">
						Geen creditcard nodig · Resultaten binnen 5 minuten · 10.000+
						tevreden gebruikers
					</p>
				</div>
			</section>

			{/* Trust Signals */}
			<section className="border-t px-4 py-12">
				<div className="container mx-auto max-w-6xl text-center">
					<p className="text-muted-foreground mb-4 text-sm">Vertrouwd door</p>
					<div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
						<span className="text-xs">10.000+ gebruikers</span>
						<span className="text-xs">4.9 sterren</span>
						<span className="text-xs">ISDE Partner</span>
						<span className="text-xs">100% Nederlands</span>
						<span className="text-xs">Bank-level beveiliging</span>
					</div>
				</div>
			</section>
		</main>
	)
}
